import { useMemo } from "react";

import { ProfileCard } from "../../../types/profile";
import { ProfileSectionConfig } from "../dashboard.types";

import {
  clampPercentage,
  parseCompletionValue,
  parseNumericValue,
} from "../dashboard.utils";

type Props = {
  profileSections: ProfileSectionConfig[];
  dashboardData: any;
  dashboardLoading: boolean;
};

const getProfileCompletion = (profile: ProfileCard): number | null => {
  const completion = parseCompletionValue(profile.completion);

  if (completion !== null) {
    return completion;
  }

  return parseCompletionValue(profile.matchScore);
};

const useBusinessScore = ({
  profileSections,
  dashboardData,
  dashboardLoading,
}: Props) => {
  return useMemo(() => {
    const completionByProfile = new Map<string | number, number>();

    profileSections.forEach((section) => {
      section.profiles.forEach((profile) => {
        const completion = getProfileCompletion(profile);

        if (completion === null) {
          return;
        }

        const identifier = profile.id ?? profile.name;

        const existing = completionByProfile.get(identifier);

        if (existing === undefined || completion > existing) {
          completionByProfile.set(identifier, completion);
        }
      });
    });

    const completionValues = Array.from(completionByProfile.values());

    const derivedCompletedProfilesCount = completionValues.filter(
      (value) => value >= 80,
    ).length;

    const derivedBrandValuePercent = derivedCompletedProfilesCount * 0.1;

    const completedProfilesFromApi = parseNumericValue(
      dashboardData?.completedProfilesCount ??
        dashboardData?.completedProfiles ??
        dashboardData?.completeProfiles ??
        dashboardData?.completion?.eightyPercentCount ??
        dashboardData?.summary?.completedProfiles ??
        dashboardData?.summary?.completeProfiles ??
        dashboardData?.metrics?.completedProfiles ??
        dashboardData?.metrics?.completeProfiles ??
        dashboardData?.profileMetrics?.completedProfiles ??
        dashboardData?.profileMetrics?.completeProfiles,
    );

    const trackedProfilesFromApi = parseNumericValue(
      dashboardData?.trackedProfilesCount ??
        dashboardData?.summary?.trackedProfiles ??
        dashboardData?.summary?.profilesWithCompleteness ??
        dashboardData?.summary?.totalProfilesWithCompleteness ??
        dashboardData?.completion?.totalProfiles ??
        dashboardData?.metrics?.trackedProfiles ??
        dashboardData?.metrics?.profilesWithCompleteness ??
        dashboardData?.profileMetrics?.trackedProfiles ??
        dashboardData?.profileMetrics?.profilesWithCompleteness,
    );

    const brandValuePercentFromApi = parseCompletionValue(
      dashboardData?.brandValuePercent ??
        dashboardData?.brandValue ??
        dashboardData?.brand_score ??
        dashboardData?.completion?.completionRate ??
        dashboardData?.summary?.brandValuePercent ??
        dashboardData?.summary?.brandValue ??
        dashboardData?.metrics?.brandValuePercent ??
        dashboardData?.metrics?.brandValue ??
        dashboardData?.profileMetrics?.brandValuePercent ??
        dashboardData?.profileMetrics?.brandValue,
    );

    const completedProfilesCount = Math.max(
      0,
      Math.round(completedProfilesFromApi ?? derivedCompletedProfilesCount),
    );

    const brandValuePercent = Math.max(
      0,
      brandValuePercentFromApi ?? derivedBrandValuePercent,
    );

    const trackedProfilesCount = Math.max(
      0,
      Math.round(trackedProfilesFromApi ?? completionValues.length),
    );

    const brandValueDisplay = dashboardLoading
      ? "--"
      : brandValuePercent === 0
        ? "0.0"
        : brandValuePercent.toFixed(1);

    const brandValueProgressWidth = dashboardLoading
      ? "0%"
      : `${clampPercentage(brandValuePercent).toFixed(1)}%`;

    const totalRegisteredProfiles = profileSections
      .filter((section) =>
        ["my-male", "my-female", "pending-male", "pending-female"].includes(
          section.key,
        ),
      )
      .reduce(
        (total, section) =>
          total +
          (parseNumericValue(section.totalCount) ?? section.profiles.length),
        0,
      );

    return {
      completedProfilesCount,
      trackedProfilesCount,
      brandValuePercent,
      brandValueDisplay,
      brandValueProgressWidth,
      totalRegisteredProfiles,
    };
  }, [dashboardData, dashboardLoading, profileSections]);
};

export default useBusinessScore;
